import React, {
  useCallback, useContext, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonBar, ScreenHeader, Select
} from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const STATE_OPTIONS = US_STATES.map(state => ({
  id: state,
  label: state
}));

export function NewPOAStateSelection() {
  const { powerOfAttorney, setPowerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  
  useScrollToTop();
  const [ selectedState, setSelectedState ] = useState(powerOfAttorney?.state_of_residence || '');

  const handleCancel = useCallback(() => {
    navigate('/home');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    setPowerOfAttorney(prev => ({ ...prev, state_of_residence: selectedState }));
    navigate('intro');
  }, [ selectedState, setPowerOfAttorney, navigate ]);

  const handleStateChange = useCallback((option: { id: string; label: string }) => {
    setSelectedState(option.id);
  }, []);

  const selectedStateOption = selectedState ? { id: selectedState, label: selectedState } : null;
  const isNextDisabled = !selectedState;

  return (
    <div className="new-poa-state-selection screen-container screen">
      <ScreenHeader
        title="Select Your State of Legal Residence"
        description="Please select the state where you legally reside. This will determine the specific requirements for your Power of Attorney document."
      />
      
      <div className="content-section">
        <div className="state-selection-container">
          <Select
            id="state-select"
            label="State of Legal Residence"
            options={STATE_OPTIONS}
            selectedOption={selectedStateOption}
            handleChange={handleStateChange}
            required
          />
        </div>

        <div className="state-info">
          <h3>Why We Need Your State</h3>
          <ul className="state-requirements">
            <li>Each state has specific legal requirements for Power of Attorney documents</li>
            <li>Your state determines witness and notarization requirements</li>
            <li>Some states have unique forms or language requirements</li>
            <li>We'll customize your document to meet your state's legal standards</li>
          </ul>
        </div>
      </div>

      <ButtonBar
        handlePrimary={handleNext}
        handleSecondary={handleCancel}
        primaryText="Continue"
        secondaryText="Cancel"
        disablePrimary={isNextDisabled}
      />
    </div>
  );
}
